import type { UIMessage } from "@tanstack/ai-react";

import type {
  GatekeeperPrecisionPair,
  GatekeeperQualificationKind,
} from "@/types";

export const GATEKEEPER_METADATA_PART_TYPE = "gatekeeper-metadata" as const;

export type GatekeeperPhase = "clarifying" | "qualified";

export interface GatekeeperMetadataPart {
  type: typeof GATEKEEPER_METADATA_PART_TYPE;
  userIntent?: "question" | "precision";
  assistantOrigin?: "gatekeeper";
  gatekeeperQualificationKind?: GatekeeperQualificationKind;
  gatekeeperPrecisions?: GatekeeperPrecisionPair[];
  gatekeeperPhase?: GatekeeperPhase;
}

export function completeGatekeeperPrecisions(
  precisions: GatekeeperPrecisionPair[] | undefined
): GatekeeperPrecisionPair[] {
  return (precisions ?? [])
    .filter(
      (pair) =>
        !pair.transmissionPending &&
        pair.outcome !== "rejected" &&
        Boolean(pair.userReply?.trim())
    )
    .map((pair) => ({
      clarification: pair.clarification,
      clarificationQuestions: pair.clarificationQuestions,
      userReply: pair.userReply!.trim(),
      structuredReply: pair.structuredReply,
    }));
}

export function readGatekeeperMetadataPart(
  msg: UIMessage
): GatekeeperMetadataPart | undefined {
  return (msg.parts as unknown[]).find(
    (p): p is GatekeeperMetadataPart =>
      typeof p === "object" &&
      p !== null &&
      (p as Record<string, unknown>).type === GATEKEEPER_METADATA_PART_TYPE
  );
}

export function withGatekeeperMetadata(
  msg: UIMessage,
  patch: Omit<GatekeeperMetadataPart, "type">
): UIMessage {
  const partsWithout = (msg.parts ?? []).filter(
    (p) =>
      typeof p !== "object" ||
      p === null ||
      (p as Record<string, unknown>).type !== GATEKEEPER_METADATA_PART_TYPE
  );
  const existing = readGatekeeperMetadataPart(msg);
  const metadataPart: GatekeeperMetadataPart = {
    type: GATEKEEPER_METADATA_PART_TYPE,
    ...existing,
    ...patch,
  };

  return {
    ...msg,
    parts: [...partsWithout, metadataPart] as UIMessage["parts"],
  };
}

export function gatekeeperMetadataToMessageFields(
  part: GatekeeperMetadataPart | undefined
): Pick<
  import("@/types").Message,
  | "userIntent"
  | "assistantOrigin"
  | "gatekeeperQualificationKind"
  | "gatekeeperPrecisions"
  | "gatekeeperPhase"
> {
  if (!part) return {};
  return {
    userIntent: part.userIntent,
    assistantOrigin: part.assistantOrigin,
    gatekeeperQualificationKind: part.gatekeeperQualificationKind,
    gatekeeperPrecisions: part.gatekeeperPrecisions,
    gatekeeperPhase: part.gatekeeperPhase,
  };
}

export function messageFieldsToGatekeeperMetadataPatch(
  msg: import("@/types").Message
): Omit<GatekeeperMetadataPart, "type"> {
  const patch: Omit<GatekeeperMetadataPart, "type"> = {};
  if (msg.userIntent) patch.userIntent = msg.userIntent;
  if (msg.assistantOrigin) patch.assistantOrigin = msg.assistantOrigin;
  if (msg.gatekeeperQualificationKind) {
    patch.gatekeeperQualificationKind = msg.gatekeeperQualificationKind;
  }
  if (msg.gatekeeperPrecisions?.length) {
    patch.gatekeeperPrecisions = msg.gatekeeperPrecisions;
  }
  if (msg.gatekeeperPhase) patch.gatekeeperPhase = msg.gatekeeperPhase;
  return patch;
}

export function isQuestionUserMessage(
  msg: Pick<import("@/types").Message, "role" | "userIntent">
): boolean {
  return msg.role === "user" && msg.userIntent !== "precision";
}

export function isGatekeeperMessageHiddenFromTranscript(
  message: Pick<
    import("@/types").Message,
    "role" | "userIntent" | "assistantOrigin" | "gatekeeperQualificationKind"
  >
): boolean {
  if (message.role === "user" && message.userIntent === "precision") {
    return true;
  }
  return (
    message.role === "assistant" &&
    message.assistantOrigin === "gatekeeper" &&
    message.gatekeeperQualificationKind === "clarification"
  );
}
